import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/api';
import axios from '../../config/axiosConfig';
import LoadingOverlay from '../LoadingOverlay';
import Toast from '../Toast';
import ConfirmDialog from '../ConfirmDialog';
import { normalizeRestaurantId } from '../../utils/restaurantUtils';
import BillPreviewModal from '../BillPreviewModal';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useRestaurantDetails } from '../../hooks/useRestaurantDetails';
import { calculateServiceCharge } from '../../utils/calculations';

const WaiterOrderManagement = ({ waiterId, restaurantId: restaurantIdProp }) => {
    // Normalize restaurantId to ensure it's always a string
    const restaurantId = normalizeRestaurantId(restaurantIdProp);
    const [orders, setOrders] = useState([]);
    const [tables, setTables] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedTable, setSelectedTable] = useState(null);
    const [cartItems, setCartItems] = useState([]);
    const [customerName, setCustomerName] = useState('');
    const [customerMobile, setCustomerMobile] = useState('');
    const [specialInstructions, setSpecialInstructions] = useState('');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [editSearchTerm, setEditSearchTerm] = useState('');
    const [showBillModal, setShowBillModal] = useState(false);
    const [billOrder, setBillOrder] = useState(null);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailAddress, setEmailAddress] = useState('');
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const { restaurant } = useAppSelector(state => state.auth);
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => {},
        confirmText: 'Confirm',
        confirmColor: 'blue'
    });

    useEffect(() => {
        if (waiterId && restaurantId) {
            fetchData();
        }
    }, [waiterId, restaurantId]);

    const fetchData = async () => {
        try {
            setLoading(true);
            // Get today's date range
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            
            // Fetch orders assigned to this waiter for today only
            const [ordersRes, tablesRes, menuRes] = await Promise.all([
                axios.get(API_ENDPOINTS.ORDERS_WAITER(restaurantId), {
                    params: { 
                        waiterId, 
                        status: 'all',
                        today: 'true' // Filter to today's orders only
                    }
                }),
                axios.get(API_ENDPOINTS.TABLES_WAITER_ASSIGNED, {
                    params: { waiterId, restaurantId }
                }),
                axios.get(API_ENDPOINTS.CUSTOMER_MENU, {
                    params: { restaurantId }
                })
            ]);
            // Sort orders by date (newest first)
            const allOrders = ordersRes.data || [];
            // Additional client-side filtering to ensure only today's orders
            const todayOrders = allOrders.filter(order => {
                const orderDate = new Date(order.orderDate);
                return orderDate >= today && orderDate < tomorrow;
            });
            setOrders(todayOrders.sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate)));
            setTables(tablesRes.data);
            setMenuItems(menuRes.data.filter(item => item.isAvailable));
        } catch (error) {
            console.error('Failed to fetch data:', error);
            showToast('Failed to load data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchAllTables = async () => {
        try {
            const response = await axios.get(API_ENDPOINTS.TABLES(restaurantId));
            return response.data || [];
        } catch (error) {
            console.error('Failed to fetch all tables:', error);
            return [];
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const handleCreateOrder = async () => {
        // Open modal directly and fetch all tables for selection
        setShowCreateModal(true);
        // Fetch all tables when opening the modal so waiter can select any table
        const allTables = await fetchAllTables();
        if (allTables.length > 0) {
            setTables(allTables);
        }
    };

    const addToCart = (menuItem) => {
        const existingItem = cartItems.find(item => item.itemId === menuItem._id);
        if (existingItem) {
            setCartItems(cartItems.map(item =>
                item.itemId === menuItem._id
                    ? { ...item, quantity: item.quantity + 1 }
                    : item
            ));
        } else {
            setCartItems([...cartItems, {
                itemId: menuItem._id,
                name: menuItem.itemName,
                quantity: 1,
                price: menuItem.itemPrice
            }]);
        }
    };

    const updateCartQuantity = (itemId, quantity) => {
        if (quantity === 0) {
            setCartItems(cartItems.filter(item => item.itemId !== itemId));
        } else {
            setCartItems(cartItems.map(item =>
                item.itemId === itemId ? { ...item, quantity } : item
            ));
        }
    };

    const { taxPercentage, calculateTaxAmount } = useRestaurantDetails(restaurantId);

    const calculateTotal = () => {
        return cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    };

    const calculateServiceAmount = () => {
        const baseAmount = calculateTotal();
        return calculateServiceCharge(baseAmount);
    };

    const calculateTax = () => {
        const baseAmount = calculateTotal();
        return parseFloat(calculateTaxAmount(baseAmount)) || 0;
    };

    const calculateFinalAmount = () => {
        const baseAmount = calculateTotal();
        const tax = calculateTax();
        const service = calculateServiceAmount();
        return baseAmount + tax + service;
    };

    const handleSubmitOrder = async () => {
        if (cartItems.length === 0) {
            showToast('Please add items to the order', 'error');
            return;
        }

        if (!selectedTable) {
            showToast('Please select a table', 'error');
            return;
        }

        setActionLoading(true);
        setLoadingMessage('Creating order...');
        try {
            // If table is not assigned to this waiter, assign it first
            if (!selectedTable.assignedWaiterId || selectedTable.assignedWaiterId.toString() !== waiterId) {
                try {
                    await axios.put(API_ENDPOINTS.TABLE_ASSIGN(selectedTable._id), {
                        waiterId: waiterId
                    });
                } catch (assignError) {
                    console.error('Failed to assign table to waiter:', assignError);
                    // Continue with order creation even if assignment fails
                }
            }

            const baseAmount = calculateTotal();
            const tax = calculateTax();
            const service = calculateServiceAmount();
            const final = calculateFinalAmount();

            const orderData = {
                restaurantId,
                tableId: selectedTable._id,
                items: cartItems.map(item => ({
                    itemId: item.itemId,
                    name: item.name,
                    quantity: item.quantity,
                    price: item.price,
                    status: 'PENDING' // Use PENDING instead of PLACED for item status
                })),
                customerName: customerName || undefined,
                mobileNumber: customerMobile || undefined,
                message: specialInstructions || undefined,
                orderType: 'table',
                assignedWaiterId: waiterId,
                status: 'PLACED',
                serviceAmount: service,
                finalAmount: final
            };

            await axios.post(API_ENDPOINTS.ORDERS_ADD, orderData);
            showToast('Order created successfully');
            setShowCreateModal(false);
            resetCreateForm();
            await fetchData();
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to create order', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const resetCreateForm = () => {
        setSelectedTable(null);
        setCartItems([]);
        setCustomerName('');
        setCustomerMobile('');
        setSpecialInstructions('');
        setSearchTerm('');
    };

    // Filter menu items based on search term
    const getFilteredMenuItems = (search) => {
        if (!search || search.trim() === '') {
            return menuItems;
        }
        const searchLower = search.toLowerCase();
        return menuItems.filter(item => 
            item.itemName.toLowerCase().includes(searchLower) ||
            (item.itemCategory && item.itemCategory.toLowerCase().includes(searchLower)) ||
            (item.itemType && item.itemType.toLowerCase().includes(searchLower))
        );
    };

    const handleEditOrder = (order) => {
        // Check if order can be edited (not PREPARING or beyond)
        if (order.status === 'PREPARING' || order.status === 'READY' || order.status === 'SERVED') {
            showToast('Cannot edit order once kitchen has started preparing', 'error');
            return;
        }
        setSelectedOrder(order);
        setCartItems(order.items || []);
        setEditSearchTerm(''); // Reset search when opening edit modal
        setShowEditModal(true);
    };

    const handleUpdateOrder = async () => {
        if (!selectedOrder) return;

        setActionLoading(true);
        setLoadingMessage('Updating order...');
        try {
            const updateData = {
                items: cartItems.map(item => ({
                    itemId: item.itemId || item._id,
                    name: item.name,
                    quantity: item.quantity,
                    price: item.price,
                    status: item.status || 'PENDING' // Use PENDING instead of PLACED for item status
                }))
            };

            await axios.put(API_ENDPOINTS.ORDER_BY_ID(selectedOrder._id), updateData);
            showToast('Order updated successfully');
            setShowEditModal(false);
            setSelectedOrder(null);
            await fetchData();
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to update order', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleMarkServed = (orderId) => {
        setConfirmDialog({
            isOpen: true,
            title: 'Mark Order as Served',
            message: 'Are you sure this order has been served to the customer?',
            confirmText: 'Mark Served',
            confirmColor: 'green',
            onConfirm: async () => {
                setActionLoading(true);
                setLoadingMessage('Updating order status...');
                try {
                    await axios.patch(API_ENDPOINTS.ORDER_BY_ID(orderId) + '/status', {
                        status: 'SERVED'
                    });
                    showToast('Order marked as served');
                    await fetchData();
                } catch (error) {
                    showToast(error.response?.data?.message || 'Failed to update order', 'error');
                } finally {
                    setActionLoading(false);
                }
            }
        });
    };

    const handleMarkDone = (order) => {
        setConfirmDialog({
            isOpen: true,
            title: 'Mark Order as Done',
            message: 'Are you sure you want to mark this order as done? This will close the order.',
            confirmText: 'Mark Done',
            confirmColor: 'blue',
            onConfirm: async () => {
                setActionLoading(true);
                setLoadingMessage('Closing order...');
                try {
                    await axios.patch(API_ENDPOINTS.ORDER_BY_ID(order._id) + '/status', {
                        status: 'CLOSED'
                    });
                    showToast('Order marked as done');
                    await fetchData();
                } catch (error) {
                    showToast(error.response?.data?.message || 'Failed to close order', 'error');
                } finally {
                    setActionLoading(false);
                }
            }
        });
    };

    const handleGenerateBill = (order) => {
        setBillOrder(order);
        setShowBillModal(true);
    };

    const handleSendBillEmail = async (order) => {
        if (!emailAddress || !emailAddress.includes('@')) {
            showToast('Please enter a valid email address', 'error');
            return;
        }

        setIsSendingEmail(true);
        setLoadingMessage('Sending bill via email...');
        try {
            await axios.get(API_ENDPOINTS.ORDER_SEND_BILL_EMAIL(order._id, emailAddress));
            showToast('Bill sent successfully via email!', 'success');
            setShowEmailModal(false);
            setEmailAddress('');
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to send bill via email', 'error');
        } finally {
            setIsSendingEmail(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'PLACED':
                return 'bg-gray-100 text-gray-800';
            case 'ACCEPTED':
                return 'bg-blue-100 text-blue-800';
            case 'PREPARING':
                return 'bg-yellow-100 text-yellow-800';
            case 'READY':
                return 'bg-green-100 text-green-800';
            case 'SERVED':
                return 'bg-purple-100 text-purple-800';
            case 'CLOSED':
                return 'bg-gray-200 text-gray-600';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getItemStatusColor = (status) => {
        switch (status) {
            case 'PENDING':
                return 'bg-gray-100 text-gray-700';
            case 'COOKING':
                return 'bg-orange-100 text-orange-700';
            case 'PREPARING':
                return 'bg-yellow-100 text-yellow-700';
            case 'READY':
                return 'bg-green-100 text-green-700';
            case 'SERVED':
                return 'bg-blue-100 text-blue-700';
            case 'DONE':
                return 'bg-purple-100 text-purple-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    const getItemStatusIcon = (status) => {
        switch (status) {
            case 'PENDING':
                return '⏳';
            case 'COOKING':
                return '👨‍🍳';
            case 'PREPARING':
                return '⏰';
            case 'READY':
                return '✅';
            case 'SERVED':
                return '🍽️';
            case 'DONE':
                return '✔️';
            default:
                return '⏳';
        }
    };

    const handleItemStatusChange = async (orderId, itemId, newStatus) => {
        setActionLoading(true);
        setLoadingMessage('Updating item status...');
        try {
            await axios.patch(API_ENDPOINTS.ORDER_UPDATE_ITEM_STATUS(orderId), {
                itemId: itemId,
                status: newStatus
            });
            showToast('Item status updated successfully');
            await fetchData();
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to update item status', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const handleOrderStatusChange = async (orderId, newStatus) => {
        // Show confirmation for critical status changes
        if (newStatus === 'CLOSED') {
            setConfirmDialog({
                isOpen: true,
                title: 'Close Order',
                message: 'Are you sure you want to close this order? This action cannot be undone.',
                confirmText: 'Close Order',
                confirmColor: 'red',
                onConfirm: async () => {
                    setActionLoading(true);
                    setLoadingMessage('Closing order...');
                    try {
                        await axios.patch(API_ENDPOINTS.ORDER_UPDATE_STATUS(orderId), {
                            status: newStatus
                        });
                        showToast('Order status updated successfully');
                        await fetchData();
                    } catch (error) {
                        showToast(error.response?.data?.message || 'Failed to update order status', 'error');
                    } finally {
                        setActionLoading(false);
                    }
                }
            });
        } else {
            setActionLoading(true);
            setLoadingMessage('Updating order status...');
            try {
                await axios.patch(API_ENDPOINTS.ORDER_UPDATE_STATUS(orderId), {
                    status: newStatus
                });
                showToast('Order status updated successfully');
                await fetchData();
            } catch (error) {
                showToast(error.response?.data?.message || 'Failed to update order status', 'error');
            } finally {
                setActionLoading(false);
            }
        }
    };

    if (loading) {
        return (
            <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
        );
    }

    return (
        <div className="relative">
            {actionLoading && <LoadingOverlay message={loadingMessage} />}

            <div className="mb-6 flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold mb-2">Order Management</h2>
                    <p className="text-gray-600">Create and manage orders for your tables</p>
                </div>
                <button
                    onClick={handleCreateOrder}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                    + Create Order
                </button>
            </div>

            {/* Orders List */}
            <div className="space-y-4">
                {orders.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <span className="text-6xl block mb-4">📋</span>
                        <p className="text-gray-600 text-lg">No active orders</p>
                    </div>
                ) : (
                    orders.map((order) => (
                        <div key={order._id} className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="text-lg font-bold">
                                        Order #{order._id.slice(-6)}
                                    </h3>
                                    <p className="text-sm text-gray-600">
                                        Table: {order.tableId?.tableNumber || order.tableNumber || 'N/A'}
                                    </p>
                                    {order.customerName && (
                                        <p className="text-sm text-gray-600">
                                            Customer: {order.customerName}
                                        </p>
                                    )}
                                    {order.mobileNumber && (
                                        <p className="text-xs text-gray-500">
                                            Contact: {order.mobileNumber}
                                        </p>
                                    )}
                                    <p className="text-xs text-gray-500">
                                        {new Date(order.orderDate).toLocaleString()}
                                    </p>
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(order.status)}`}>
                                        {order.status}
                                    </span>
                                    {order.status !== 'CLOSED' && (
                                        <select
                                            value={order.status}
                                            onChange={(e) => handleOrderStatusChange(order._id, e.target.value)}
                                            className="text-xs px-2 py-1 border rounded bg-white font-medium cursor-pointer hover:bg-gray-50"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <option value="PLACED">Placed</option>
                                            <option value="ACCEPTED">Accepted</option>
                                            <option value="PREPARING">Preparing</option>
                                            <option value="READY">Ready</option>
                                            <option value="SERVED">Served</option>
                                            <option value="CLOSED">Closed</option>
                                        </select>
                                    )}
                                </div>
                            </div>

                            <div className="mb-4">
                                <p className="text-sm font-semibold mb-2">Items:</p>
                                <div className="space-y-2">
                                    {order.items?.map((item, idx) => {
                                        const itemId = item._id || item.itemId || idx;
                                        return (
                                            <div key={idx} className="flex justify-between items-center text-sm bg-gray-50 p-2 rounded">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <span>{item.name} x {item.quantity}</span>
                                                        <span className={`px-2 py-1 rounded text-xs font-semibold ${getItemStatusColor(item.status || 'PENDING')}`}>
                                                            {getItemStatusIcon(item.status || 'PENDING')} {item.status || 'PENDING'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium">₹{item.price * item.quantity}</span>
                                                    {order.status !== 'CLOSED' && (
                                                        <select
                                                            value={item.status || 'PENDING'}
                                                            onChange={(e) => handleItemStatusChange(order._id, itemId, e.target.value)}
                                                            className="text-xs px-2 py-1 border rounded bg-white"
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <option value="PENDING">Pending</option>
                                                            <option value="COOKING">Cooking</option>
                                                            <option value="PREPARING">Preparing</option>
                                                            <option value="READY">Ready</option>
                                                            <option value="SERVED">Served</option>
                                                            <option value="DONE">Done</option>
                                                        </select>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {order.message && (
                                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <p className="text-xs font-semibold text-yellow-800 mb-1">Special Instructions:</p>
                                    <p className="text-sm text-yellow-700">{order.message}</p>
                                </div>
                            )}

                            <div className="flex justify-between items-center mb-4">
                                <div>
                                    <span className="font-bold">Total: ₹{order.finalAmount || order.totalAmount || 0}</span>
                                    {order.taxAmount > 0 && (
                                        <p className="text-xs text-gray-500">
                                            Tax: ₹{order.taxAmount} | Service: ₹{order.serviceAmount || 0}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="flex gap-2 flex-wrap mt-4">
                                {/* Edit Order - Only for PLACED and ACCEPTED */}
                                {(order.status === 'PLACED' || order.status === 'ACCEPTED') && (
                                    <button
                                        onClick={() => handleEditOrder(order)}
                                        className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                                    >
                                        ✏️ Edit Order
                                    </button>
                                )}

                                {/* Mark as Done - Show for SERVED and after Edit Order */}
                                {(order.status === 'SERVED' || order.status === 'PLACED' || order.status === 'ACCEPTED' || order.status === 'PREPARING' || order.status === 'READY') && (
                                    <button
                                        onClick={() => handleMarkDone(order)}
                                        className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                                    >
                                        ✓ Mark as Done
                                    </button>
                                )}

                                {/* Generate Bill - Show for all statuses after Edit Order */}
                                <button
                                    onClick={() => handleGenerateBill(order)}
                                    className="flex-1 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
                                >
                                    {order.status === 'CLOSED' ? '🧾 View Bill' : '🧾 Generate Bill'}
                                </button>

                                {/* Send Bill via Email - Show for all statuses after Edit Order */}
                                <button
                                    onClick={() => {
                                        setBillOrder(order);
                                        setShowEmailModal(true);
                                    }}
                                    className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
                                >
                                    📧 Send Bill (Email)
                                </button>

                                {/* Mark as Served - For READY status */}
                                {order.status === 'READY' && (
                                    <button
                                        onClick={() => handleMarkServed(order._id)}
                                        className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                                    >
                                        ✓ Mark as Served
                                    </button>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Create Order Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <h3 className="text-2xl font-bold mb-4">Create New Order</h3>

                            {/* Table Selection */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2">
                                    Select Table <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={selectedTable?._id || ''}
                                    onChange={(e) => {
                                        const table = tables.find(t => t._id === e.target.value);
                                        setSelectedTable(table);
                                    }}
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                >
                                    <option value="">Select a table</option>
                                    {tables.map(table => (
                                        <option key={table._id} value={table._id}>
                                            Table {table.tableNumber} - {table.status}
                                            {table.assignedWaiterId && table.assignedWaiterId.toString() !== waiterId 
                                                ? ' (Assigned to another waiter)' 
                                                : table.assignedWaiterId && table.assignedWaiterId.toString() === waiterId
                                                ? ' (Your table)'
                                                : ' (Available)'}
                                        </option>
                                    ))}
                                </select>
                                {selectedTable && (
                                    <p className="mt-1 text-xs text-gray-600">
                                        {selectedTable.assignedWaiterId && selectedTable.assignedWaiterId.toString() !== waiterId
                                            ? '⚠️ This table is assigned to another waiter. You will be assigned when creating the order.'
                                            : selectedTable.assignedWaiterId && selectedTable.assignedWaiterId.toString() === waiterId
                                            ? '✅ This is your assigned table.'
                                            : 'ℹ️ This table will be assigned to you when creating the order.'}
                                    </p>
                                )}
                            </div>

                            {/* Customer Info */}
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Customer Name (Optional)</label>
                                    <input
                                        type="text"
                                        value={customerName}
                                        onChange={(e) => setCustomerName(e.target.value)}
                                        className="w-full px-4 py-2 border rounded-lg"
                                        placeholder="Enter customer name"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2">Mobile (Optional)</label>
                                    <input
                                        type="text"
                                        value={customerMobile}
                                        onChange={(e) => setCustomerMobile(e.target.value)}
                                        className="w-full px-4 py-2 border rounded-lg"
                                        placeholder="Enter mobile number"
                                    />
                                </div>
                            </div>

                            {/* Menu Items */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2">Menu Items</label>
                                
                                {/* Search Bar */}
                                <div className="mb-3">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="Search food items..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                        <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
                                        {searchTerm && (
                                            <button
                                                onClick={() => setSearchTerm('')}
                                                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                                                title="Clear search"
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                    {searchTerm && (
                                        <p className="mt-1 text-xs text-gray-500">
                                            Showing {getFilteredMenuItems(searchTerm).length} of {menuItems.length} items
                                        </p>
                                    )}
                                </div>
                                
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-60 overflow-y-auto border rounded-lg p-2">
                                    {getFilteredMenuItems(searchTerm).length === 0 ? (
                                        <div className="col-span-full text-center py-4 text-gray-500 text-sm">
                                            No items found matching "{searchTerm}"
                                        </div>
                                    ) : (
                                        getFilteredMenuItems(searchTerm).map(item => (
                                            <button
                                                key={item._id}
                                                onClick={() => addToCart(item)}
                                                className="p-2 border rounded hover:bg-blue-50 text-left transition-colors"
                                            >
                                                <div className="font-medium text-sm">{item.itemName}</div>
                                                <div className="text-xs text-gray-600">₹{item.itemPrice}</div>
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Cart */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2">Order Items</label>
                                {cartItems.length === 0 ? (
                                    <p className="text-gray-500 text-sm">No items added yet</p>
                                ) : (
                                    <div className="space-y-2 border rounded-lg p-3">
                                        {cartItems.map((item, idx) => (
                                            <div key={idx} className="flex justify-between items-center">
                                                <span className="text-sm">{item.name} x {item.quantity}</span>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => updateCartQuantity(item.itemId, item.quantity - 1)}
                                                        className="px-2 py-1 bg-gray-200 rounded"
                                                    >
                                                        -
                                                    </button>
                                                    <span className="w-8 text-center">{item.quantity}</span>
                                                    <button
                                                        onClick={() => updateCartQuantity(item.itemId, item.quantity + 1)}
                                                        className="px-2 py-1 bg-gray-200 rounded"
                                                    >
                                                        +
                                                    </button>
                                                    <span className="ml-4 font-medium">₹{item.price * item.quantity}</span>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="border-t pt-2 mt-2 space-y-1">
                                            <div className="flex justify-between text-sm">
                                                <span>Subtotal:</span>
                                                <span>₹{calculateTotal().toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span>Tax ({taxPercentage || 0}%):</span>
                                                <span>₹{calculateTax().toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between text-sm">
                                                <span>Platform Fees:</span>
                                                <span>₹{calculateServiceAmount().toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between font-bold pt-1 border-t">
                                                <span>Total:</span>
                                                <span className="text-green-600">₹{calculateFinalAmount().toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Special Instructions */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2">Special Instructions</label>
                                <textarea
                                    value={specialInstructions}
                                    onChange={(e) => setSpecialInstructions(e.target.value)}
                                    className="w-full px-4 py-2 border rounded-lg"
                                    placeholder="e.g., Less spicy, No onion, etc."
                                    rows="3"
                                />
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        resetCreateForm();
                                    }}
                                    className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmitOrder}
                                    disabled={cartItems.length === 0 || !selectedTable}
                                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                >
                                    Create Order
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Order Modal */}
            {showEditModal && selectedOrder && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <h3 className="text-2xl font-bold mb-4">Edit Order</h3>

                            {/* Menu Items */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2">Menu Items</label>
                                
                                {/* Search Bar */}
                                <div className="mb-3">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="Search food items..."
                                            value={editSearchTerm}
                                            onChange={(e) => setEditSearchTerm(e.target.value)}
                                            className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                        <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
                                        {editSearchTerm && (
                                            <button
                                                onClick={() => setEditSearchTerm('')}
                                                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                                                title="Clear search"
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                    {editSearchTerm && (
                                        <p className="mt-1 text-xs text-gray-500">
                                            Showing {getFilteredMenuItems(editSearchTerm).length} of {menuItems.length} items
                                        </p>
                                    )}
                                </div>
                                
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-60 overflow-y-auto border rounded-lg p-2">
                                    {getFilteredMenuItems(editSearchTerm).length === 0 ? (
                                        <div className="col-span-full text-center py-4 text-gray-500 text-sm">
                                            No items found matching "{editSearchTerm}"
                                        </div>
                                    ) : (
                                        getFilteredMenuItems(editSearchTerm).map(item => (
                                            <button
                                                key={item._id}
                                                onClick={() => addToCart(item)}
                                                className="p-2 border rounded hover:bg-blue-50 text-left transition-colors"
                                            >
                                                <div className="font-medium text-sm">{item.itemName}</div>
                                                <div className="text-xs text-gray-600">₹{item.itemPrice}</div>
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Cart */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-2">Order Items</label>
                                <div className="space-y-2 border rounded-lg p-3">
                                    {cartItems.map((item, idx) => (
                                        <div key={idx} className="flex justify-between items-center">
                                            <span className="text-sm">{item.name} x {item.quantity}</span>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => updateCartQuantity(item.itemId || item._id, item.quantity - 1)}
                                                    className="px-2 py-1 bg-gray-200 rounded"
                                                >
                                                    -
                                                </button>
                                                <span className="w-8 text-center">{item.quantity}</span>
                                                <button
                                                    onClick={() => updateCartQuantity(item.itemId || item._id, item.quantity + 1)}
                                                    className="px-2 py-1 bg-gray-200 rounded"
                                                >
                                                    +
                                                </button>
                                                <span className="ml-4 font-medium">₹{item.price * item.quantity}</span>
                                            </div>
                                        </div>
                                    ))}
                                    <div className="border-t pt-2 mt-2 flex justify-between font-bold">
                                        <span>Total:</span>
                                        <span>₹{calculateTotal()}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        setShowEditModal(false);
                                        setSelectedOrder(null);
                                        setCartItems([]);
                                        setEditSearchTerm('');
                                    }}
                                    className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpdateOrder}
                                    disabled={cartItems.length === 0}
                                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                >
                                    Update Order
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showBillModal && billOrder && (
                <BillPreviewModal
                    isOpen={showBillModal}
                    onClose={() => {
                        setShowBillModal(false);
                        setBillOrder(null);
                    }}
                    order={billOrder}
                    orderId={billOrder._id}
                    tryServerPrint
                    onNotify={showToast}
                    restaurantName={
                        restaurant?.name ||
                        JSON.parse(localStorage.getItem('restaurantData') || '{}')?.name
                    }
                />
            )}

            {/* Email Modal */}
            {showEmailModal && billOrder && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                        <div className="p-6">
                            <h3 className="text-xl font-bold mb-4 text-gray-800">Send Bill via Email</h3>
                            <p className="text-gray-600 mb-4 text-sm">
                                Enter the customer's email address to send the bill as a PDF attachment.
                            </p>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    placeholder="customer@example.com"
                                    value={emailAddress}
                                    onChange={(e) => setEmailAddress(e.target.value)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' && !isSendingEmail) {
                                            handleSendBillEmail(billOrder);
                                        }
                                    }}
                                />
                            </div>
                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={() => {
                                        setShowEmailModal(false);
                                        setEmailAddress('');
                                        setBillOrder(null);
                                    }}
                                    disabled={isSendingEmail}
                                    className="px-4 py-2 bg-gray-300 hover:bg-gray-400 disabled:bg-gray-200 text-gray-800 rounded-lg transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleSendBillEmail(billOrder)}
                                    disabled={isSendingEmail || !emailAddress || !emailAddress.includes('@')}
                                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded-lg transition-colors font-medium"
                                >
                                    {isSendingEmail ? 'Sending...' : 'Send Email'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {toast.show && <Toast message={toast.message} type={toast.type} />}
            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
                onConfirm={confirmDialog.onConfirm}
                title={confirmDialog.title}
                message={confirmDialog.message}
                confirmText={confirmDialog.confirmText}
                confirmColor={confirmDialog.confirmColor}
            />
        </div>
    );
};

export default WaiterOrderManagement;

